import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import PersonalInfoForm, { PersonalInfo } from '~components/uas/PersonalInfoForm';
import PhoneVerification from '~components/uas/PhoneVerification';
import StudentInfoForm, { StudentInfo } from '~components/uas/StudentInfoForm';
import Success, { Taker } from '~components/uas/Success';
import Layout from '~components/ui/Layout';
import Loading from '~components/ui/Loading';
import Steps from '~components/ui/Steps';
import { getFromAPI, postToAPI } from '~utils/api/common';

import logoUas from '../public/img/uas_logo.png';

export const getStaticProps: GetStaticProps = async () => {
  const schools = await getFromAPI('/uas-students/schools-and-majors');
  return {
    props: {
      schools,
    },
    revalidate: 60 * 60 * 24, // Each 24 hours
  };
};

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const UasPage: NextPage<Props> = ({ schools }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [studentData, setStudentData] = useState<StudentInfo>();
  const [personalData, setPersonalData] = useState<PersonalInfo>();
  const [taker, setTaker] = useState<Taker>();

  const addTaker = (verificationCode: string) => {
    setLoading(true);
    postToAPI('/uas-students/create-taker', { ...studentData, ...personalData, verificationCode })
      .then((response) => {
        setTaker(response);
        setStep(4);
      })
      .finally(() => setLoading(false));
  };
  return (
    <Layout
      meta={{ title: 'Registro estudiantes UAS', description: 'Registro estudiantes UAS' }}
      headerType="opaque"
    >
      <section className="mx-auto max-w-2xl flex flex-col items-center space-y-8 mt-4 mb-12 px-4">
        <Image src={logoUas} alt="logo UAS" width={100} />
        <Steps step={step} />
        <Loading show={loading} label="Validando código" containerClassName="w-full">
          <div className="rounded-xl border dark:border-neutral-600">
            {step === 1 && (
              <StudentInfoForm
                schools={schools}
                onContinue={(data) => {
                  setStudentData(data);
                  setStep(2);
                }}
              />
            )}
            {step === 2 && (
              <PersonalInfoForm
                onContinue={(data) => {
                  setPersonalData(data);
                  setStep(3);
                }}
                onBack={() => setStep(1)}
              />
            )}
            {step === 3 && personalData && (
              <PhoneVerification
                phone={personalData.phone}
                name={personalData.name}
                onContinue={(code) => addTaker(code)}
                onBack={() => setStep(2)}
              />
            )}
            {step === 4 && taker && studentData && <Success data={{ ...taker, ...studentData }} />}
          </div>
        </Loading>
        <div className="w-full rounded-xl bg-accent p-4 text-center">
          <p className="mb-3 text-foreground">¿Eres colaborador UAS?</p>
          <Link href="/colaboradores-uas" className="btn btn-secondary justify-center font-bold">
            Ir al registro de colaboradores
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default UasPage;
